import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { supabase } from "@/integrations/supabase/client";

interface MFAChallengeProps {
  onSuccess: () => void;
  onCancel: () => void;
}

export function MFAChallenge({ onSuccess, onCancel }: MFAChallengeProps) {
  const [code, setCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const { verifyMFA } = useAuth();

  const handleVerify = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (code.length !== 6 || isLoading) return;

    setIsLoading(true);
    try {
      const { data: factors, error: factorsError } = await supabase.auth.mfa.listFactors();
      if (factorsError) throw factorsError;

      const totpFactor = factors.all.find(
        (f) => f.factor_type === "totp" && f.status === "verified"
      );

      if (!totpFactor) {
        toast.error("No verified TOTP factor found. Please contact your administrator.");
        onCancel();
        return;
      }

      const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId: totpFactor.id,
      });

      if (challengeError) throw challengeError;

      const { error: verifyError } = await verifyMFA(
        totpFactor.id,
        challenge.id,
        code
      );

      if (verifyError) {
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);
        setCode("");

        if (newAttempts >= 3) {
          toast.error(
            "Invalid code. Please ensure your phone's clock is set to automatic time and you're using the correct authenticator entry.",
            { duration: 6000 }
          );
        } else {
          toast.error("Invalid code. Please wait for a fresh code and try again.");
        }
      } else {
        toast.success("MFA verified successfully");
        onSuccess();
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to verify MFA");
      setCode("");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6 flex flex-col items-center">
      <div className="text-center space-y-2">
        <h2 className="text-xl font-semibold">Two-Factor Authentication</h2>
        <p className="text-sm text-muted-foreground">
          Enter the 6-digit code from your authenticator app
        </p>
      </div>

      <form onSubmit={handleVerify} className="space-y-6 w-full flex flex-col items-center">
        <InputOTP
          maxLength={6}
          value={code}
          onChange={(value) => setCode(value)}
          onComplete={() => handleVerify()}
          autoFocus
        >
          <InputOTPGroup>
            <InputOTPSlot index={0} />
            <InputOTPSlot index={1} />
            <InputOTPSlot index={2} />
            <InputOTPSlot index={3} />
            <InputOTPSlot index={4} />
            <InputOTPSlot index={5} />
          </InputOTPGroup>
        </InputOTP>

        <div className="flex flex-col w-full gap-2">
          <Button type="submit" className="w-full" disabled={isLoading || code.length !== 6}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Verify
          </Button>
          <Button variant="ghost" type="button" onClick={onCancel} className="w-full">
            Back to Login
          </Button>
        </div>
      </form>
    </div>
  );
}
